import { inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { BRANDING_LOGO_API_PATH, BRANDING_LOGO_TYPES, BrandingApiService } from './branding-api.service';

export const DEFAULT_LOGO_PATH = '/assets/img/TestSolutions_Logo_white.png';

/**
 * `logoPath` ist der Backend-Wert, `logoSrc` die anzuzeigende Adresse.
 * Ein hochgeladenes Logo braucht den Token und kommt deshalb per HttpClient als Blob-URL – ein `<img>` schickt keinen.
 */
@Injectable({ providedIn: 'root' })
export class HeaderLogoService {
  private brandingApi = inject(BrandingApiService);
  private loading?: Subscription;
  private blobUrl: string | null = null;

  readonly logoPath = signal<string>(DEFAULT_LOGO_PATH);
  readonly logoSrc = signal<string>(DEFAULT_LOGO_PATH);

  loadFromBackend(backendLogoPath?: string | null): void {
    const path = backendLogoPath ?? DEFAULT_LOGO_PATH;
    this.loading?.unsubscribe();
    this.logoPath.set(path);
    if (path !== BRANDING_LOGO_API_PATH) {
      this.show(path);
      return;
    }
    this.loading = this.brandingApi.getLogo(path).subscribe({
      // Nur Bildtypen als Blob-URL: ein anderer Typ (etwa SVG) liefe beim Öffnen der URL in der FE-Origin.
      next: (blob) =>
        BRANDING_LOGO_TYPES.includes(blob.type)
          ? this.show(URL.createObjectURL(blob), true)
          : this.show(DEFAULT_LOGO_PATH),
      error: () => this.show(DEFAULT_LOGO_PATH),
    });
  }

  resetToDefault(): void {
    this.loading?.unsubscribe();
    this.logoPath.set(DEFAULT_LOGO_PATH);
    this.show(DEFAULT_LOGO_PATH);
  }

  private show(src: string, isBlob = false): void {
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl);
    this.blobUrl = isBlob ? src : null;
    this.logoSrc.set(src);
  }
}
